import * as Actions from './SessionsActions';
import {
  normalizeSessions,
  computeSessionStats,
  getUpdatedStatsOnToggleAllSessionKeys,
  getUpdatedStatsOnToggleSessionKey,
  getFilteredSessionKeys,
  getCurrentSession
} from './SessionsUtils';
import { MAX_RANGE, MIN_RANGE } from '../../Utils/Constants';
import { getDifference, getUnion, sortArrayBasedOnArray } from '../../Utils/ArrayUtils';

const initialState = {
  fetching: false,
  filtering: false,
  trendFetching: null,
  sessions: {
    byKey: {},
    allKeys: [],
    deSelectedKeys: [],
    filteredKeys: [],
    filteredSelectedSessionKeys: []
  },
  filterReportSessions: {
    byKey: {},
    allKeys: [],
    deSelectedKeys: [],
    filteredKeys: [],
    filteredSelectedSessionKeys: []
  },
  uniqueTableId: '',
  filter: {
    fromTime: null,
    toTime: null,
    searchString: '',
    registrantRange: [MIN_RANGE, MAX_RANGE],
    attendeeRange: [MIN_RANGE, MAX_RANGE]
  },
  stats: {
    filteredTotalRegistrants: 0,
    filteredTotalAttendees: 0,
    filteredTotalQAndACount: 0,
    filteredTotalEngagementCount: 0,
    filteredSelectedTotalRegistrants: 0,
    filteredSelectedTotalAttendees: 0,
    filteredSelectedTotalQAndACount: 0,
    filteredSelectedTotalEngagementCount: 0
  },
  filterSessionStats: {
    filteredTotalRegistrants: 0,
    filteredTotalAttendees: 0,
    filteredTotalQAndACount: 0,
    filteredTotalEngagementCount: 0,
    filteredSelectedTotalRegistrants: 0,
    filteredSelectedTotalAttendees: 0,
    filteredSelectedTotalQAndACount: 0,
    filteredSelectedTotalEngagementCount: 0
  },
  trend: {
    totalRegistrants: 0,
    totalAttendees: 0,
    prevDateRange: {
      previousFromTime: null,
      previousToTime: null
    }
  },
  error: null
};

export default (state = initialState, action = {}) => {
  switch (action.type) {
    case Actions.POST_UNIQUE_TABLE_ID: {
      const { uniqueTableId } = action.payload;
      return {
        ...state,
        uniqueTableId: uniqueTableId
      };
    }

    case Actions.GET_SESSIONS_REQUEST: {
      return {
        ...state,
        filter: {
          ...state.filter,
          fromTime: action.payload.fromTime,
          toTime: action.payload.toTime
        },
        fetching: true,
        error: null
      };
    }

    case Actions.GET_SESSIONS_SUCCESS: {
      const { limit, sessions = [] } = action.payload;
      const { allKeys, byKey } = normalizeSessions(sessions);
      let { fromTime, toTime } = state.filter;
      if (limit && limit > 0) {
        const { 0: lastSession = {}, [sessions.length - 1]: firstSession = {} } = sessions;
        fromTime = firstSession.startTime ? firstSession.startTime : state.filter.fromTime;
        toTime = lastSession.startTime ? lastSession.startTime : state.filter.toTime;
      }

      const {
        registrantRange = state.filter.registrantRange,
        attendeeRange = state.filter.attendeeRange,
        searchString = state.filter.searchString
      } = action.payload;

      let sessionData = state.sessions;
      const filteredKeys = getFilteredSessionKeys(byKey, registrantRange, attendeeRange, searchString, allKeys);
      const newFilterSelectedSessionKeys = getDifference(filteredKeys, sessionData.deSelectedKeys);
      const computedStats = computeSessionStats(byKey, newFilterSelectedSessionKeys);

      let filterSession = state.filterReportSessions;
      const newFilterDeSelectedSessionKeys = getDifference(filteredKeys, filterSession.filteredSelectedSessionKeys);
      const newFilteredSelectedSessionKeysSecond = getDifference(filteredKeys, newFilterDeSelectedSessionKeys);
      const computedStatsSecond = computeSessionStats(byKey, newFilterDeSelectedSessionKeys);

      return {
        ...state,
        fetching: false,
        trendFetching: true,
        sessions: {
          ...state.sessions,
          byKey,
          allKeys,
          filteredKeys: filteredKeys,
          filteredSelectedSessionKeys: newFilterSelectedSessionKeys
        },
        filterReportSessions: {
          ...state.filterReportSessions,
          byKey,
          allKeys,
          filteredKeys: filteredKeys,
          deSelectedKeys: newFilterDeSelectedSessionKeys,
          filteredSelectedSessionKeys: newFilteredSelectedSessionKeysSecond
        },
        filter: {
          ...state.filter,
          fromTime: fromTime,
          toTime: toTime
        },
        filterSessionStats: {
          ...state.filterSessionStats,
          filteredTotalRegistrants: computedStatsSecond.totalRegistrants,
          filteredTotalAttendees: computedStatsSecond.totalAttendees,
          filteredTotalQAndACount: computedStatsSecond.totalQAndACount,
          filteredTotalEngagementCount: computedStatsSecond.totalEngagementCount
        },
        stats: {
          ...state.stats,
          ...{
            filteredTotalRegistrants: computedStats.totalRegistrants,
            filteredTotalAttendees: computedStats.totalAttendees,
            filteredTotalQAndACount: computedStats.totalQAndACount,
            filteredTotalEngagementCount: computedStats.totalEngagementCount,
            filteredSelectedTotalRegistrants: computedStats.totalRegistrants,
            filteredSelectedTotalAttendees: computedStats.totalAttendees,
            filteredSelectedTotalQAndACount: computedStats.totalQAndACount,
            filteredSelectedTotalEngagementCount: computedStats.totalEngagementCount
          }
        }
      };
    }

    case Actions.GET_SESSIONS_FAILURE:
      return { ...state, ...initialState, fetching: false, error: action.error };

    case Actions.GET_SESSIONS_TRENDS_REQUEST: {
      return {
        ...state,
        filter: {
          ...state.filter,
          fromTime: action.payload.fromTime,
          toTime: action.payload.toTime
        },
        trendFetching: true,
        error: null
      };
    }

    case Actions.GET_SESSIONS_TRENDS_SUCCESS: {
      const { totalRegistrants, totalAttendees, prevDateRange } = action.payload.trend;
      return {
        ...state,
        fetching: false,
        trendFetching: false,
        trend: {
          totalRegistrants: totalRegistrants,
          totalAttendees: totalAttendees,
          prevDateRange: {
            previousFromTime: prevDateRange.previousFromTime,
            previousToTime: prevDateRange.previousToTime
          }
        }
      };
    }

    case Actions.GET_SESSIONS_TRENDS_FAILURE:
      return { ...state, trendFetching: false, error: action.error };

    case Actions.TOGGLE_SESSION: {
      const { sessionKey, isSelected } = action.payload;

      const computedSession = getCurrentSession(state);
      let data = computedSession.currentSession;
      let filterReportFlag = computedSession.inGenerateReport;

      const { deSelectedKeys: existingDeSelectedKeys, filteredKeys, allKeys } = data;
      const newDeSelectedKeys = isSelected
        ? existingDeSelectedKeys.filter(key => key !== sessionKey)
        : sortArrayBasedOnArray([...existingDeSelectedKeys, sessionKey], allKeys);
      const newFilterSelectedSessionKeys = getDifference(filteredKeys, newDeSelectedKeys);

      return {
        ...state,
        sessions: {
          ...state.sessions,
          deSelectedKeys: filterReportFlag ? state.sessions.deSelectedKeys : newDeSelectedKeys,
          filteredSelectedSessionKeys: filterReportFlag
            ? state.sessions.filteredSelectedSessionKeys
            : newFilterSelectedSessionKeys
        },
        filterReportSessions: {
          ...state.filterReportSessions,
          deSelectedKeys: filterReportFlag ? newDeSelectedKeys : state.filterReportSessions.deSelectedKeys,
          filteredSelectedSessionKeys: filterReportFlag
            ? newFilterSelectedSessionKeys
            : state.filterReportSessions.filteredSelectedSessionKeys
        },
        filterSessionStats: {
          ...state.filterSessionStats,
          ...(filterReportFlag
            ? getUpdatedStatsOnToggleSessionKey(state.filterSessionStats, isSelected, data.byKey[sessionKey])
            : state.filterSessionStats)
        },
        stats: {
          ...state.stats,
          ...(filterReportFlag
            ? state.stats
            : getUpdatedStatsOnToggleSessionKey(state.stats, isSelected, data.byKey[sessionKey]))
        }
      };
    }

    case Actions.TOGGLE_ALL_SESSIONS: {
      const computedSession = getCurrentSession(state);
      let data = computedSession.currentSession;
      let filterReportFlag = computedSession.inGenerateReport;

      const { deSelectedKeys: existingDeSelectedKeys, filteredKeys: existingFilteredKeys } = data;
      const newDeSelectedKeys = action.payload.isSelected
        ? getDifference(existingDeSelectedKeys, existingFilteredKeys)
        : getUnion(existingDeSelectedKeys, existingFilteredKeys);
      const newFilterSelectedSessionKeys = getDifference(existingFilteredKeys, newDeSelectedKeys);
      return {
        ...state,
        sessions: {
          ...state.sessions,
          deSelectedKeys: filterReportFlag ? state.sessions.deSelectedKeys : newDeSelectedKeys,
          filteredSelectedSessionKeys: filterReportFlag
            ? state.sessions.filteredSelectedSessionKeys
            : newFilterSelectedSessionKeys
        },
        filterReportSessions: {
          ...state.filterReportSessions,
          deSelectedKeys: filterReportFlag ? newDeSelectedKeys : state.filterReportSessions.deSelectedKeys,
          filteredSelectedSessionKeys: filterReportFlag
            ? newFilterSelectedSessionKeys
            : state.filterReportSessions.filteredSelectedSessionKeys
        },
        filterSessionStats: {
          ...state.filterSessionStats,
          ...(filterReportFlag
            ? getUpdatedStatsOnToggleAllSessionKeys(state.filterSessionStats, action.payload.isSelected)
            : state.filterSessionStats)
        },
        stats: {
          ...state.stats,
          ...(filterReportFlag
            ? state.stats
            : getUpdatedStatsOnToggleAllSessionKeys(state.stats, action.payload.isSelected))
        }
      };
    }

    case Actions.FILTER_SESSIONS_BY_COUNT_REQUEST: {
      return {
        ...state,
        filtering: true,
        error: null
      };
    }

    case Actions.FILTER_SESSIONS_BY_COUNT: {
      const {
        registrantRange = state.filter.registrantRange,
        attendeeRange = state.filter.attendeeRange,
        searchString = state.filter.searchString
      } = action.payload;

      const { allKeys, byKey, deSelectedKeys } = state.sessions;
      const filteredKeys = getFilteredSessionKeys(byKey, registrantRange, attendeeRange, searchString, allKeys);
      const newFilterSelectedSessionKeys = getDifference(filteredKeys, deSelectedKeys);
      const computedStats = computeSessionStats(state.sessions.byKey, newFilterSelectedSessionKeys);

      const sessionAllKeys = state.filterReportSessions.allKeys;
      const sessionByKeys = state.filterReportSessions.byKey;
      const sessionDeSelectedKeys = state.filterReportSessions.deSelectedKeys;

      const filteredKeysSecond = getFilteredSessionKeys(
        sessionByKeys,
        registrantRange,
        attendeeRange,
        searchString,
        sessionAllKeys
      );
      const newFilterSelectedSessionKeysSecond = getDifference(filteredKeysSecond, sessionDeSelectedKeys);
      const computedStatsSecond = computeSessionStats(sessionByKeys, newFilterSelectedSessionKeysSecond);

      return {
        ...state,
        filtering: false,
        sessions: {
          ...state.sessions,
          filteredKeys: filteredKeys,
          filteredSelectedSessionKeys: newFilterSelectedSessionKeys
        },
        filterReportSessions: {
          ...state.filterReportSessions,
          filteredKeys: filteredKeysSecond,
          filteredSelectedSessionKeys: newFilterSelectedSessionKeysSecond
        },
        filter: {
          ...state.filter,
          registrantRange: registrantRange,
          attendeeRange: attendeeRange,
          searchString: searchString
        },
        filterSessionStats: {
          ...state.filterSessionStats,
          filteredTotalRegistrants: computedStatsSecond.totalRegistrants,
          filteredTotalAttendees: computedStatsSecond.totalAttendees,
          filteredTotalQAndACount: computedStatsSecond.totalQAndACount,
          filteredTotalEngagementCount: computedStatsSecond.totalEngagementCount,
          filteredSelectedTotalRegistrants: computedStatsSecond.totalRegistrants,
          filteredSelectedTotalAttendees: computedStatsSecond.totalAttendees,
          filteredSelectedTotalQAndACount: computedStatsSecond.totalQAndACount,
          filteredSelectedTotalEngagementCount: computedStatsSecond.totalEngagementCount
        },
        stats: {
          ...state.stats,
          filteredTotalRegistrants: computedStats.totalRegistrants,
          filteredTotalAttendees: computedStats.totalAttendees,
          filteredTotalQAndACount: computedStats.totalQAndACount,
          filteredTotalEngagementCount: computedStats.totalEngagementCount,
          filteredSelectedTotalRegistrants: computedStats.totalRegistrants,
          filteredSelectedTotalAttendees: computedStats.totalAttendees,
          filteredSelectedTotalQAndACount: computedStats.totalQAndACount,
          filteredSelectedTotalEngagementCount: computedStats.totalEngagementCount
        }
      };
    }
    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./src/Reducks/Sessions/SessionsReducer.js